import { getTrackingLink } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { fgColumnEntries } from "@redotech/redo-fulfillment-common/views/fulfillment/fg-column-catalog";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { ColumnAlignment } from "@redotech/redo-web/table";
import { Column2 } from "@redotech/redo-web/table2";
import { Text } from "@redotech/redo-web/text";
import * as styles from "./tracking-number-column.module.css";

const catalogEntry = fgColumnEntries.trackingNumber;

export const TrackingNumberColumn: Column2<FulfillmentGroupData> = {
  key: catalogEntry.key,
  title: catalogEntry.title,
  width: catalogEntry.defaultWidth,
  renderNormalCell: ({ row }) => {
    const trackingNumbers = getTrackingNumbers(row);
    if (!trackingNumbers.length) {
      return null;
    }
    const trackingLinks = groupDataToTrackingLinks(row);
    return (
      <Flex align="center" gap="sm" pl="lg">
        {trackingNumbers.map((trackingNumber, index) => (
          <Text
            className={styles.link}
            color="secondary"
            fontSize="xs"
            key={trackingNumber}
            onClick={() => {
              window.open(trackingLinks[index], "_blank");
            }}
          >
            {`#${trackingNumber}`}
          </Text>
        ))}
        <RedoButton
          IconLeading={CopyIcon}
          onClick={async () => {
            await navigator.clipboard.writeText(trackingNumbers.join(", "));
          }}
          size="xs"
        />
      </Flex>
    );
  },
  alignment: ColumnAlignment.LEFT,
};

export function getTrackingNumbers(row: FulfillmentGroupData): string[] {
  const trackingNumbers = [];

  if (row.shipment?.tracker?.trackingCode) {
    trackingNumbers.push(row.shipment?.tracker?.trackingCode);
    for (const additionalShipment of row.additionalShipments ?? []) {
      if (additionalShipment.tracker?.trackingCode) {
        trackingNumbers.push(additionalShipment.tracker?.trackingCode);
      }
    }
  } else if (row.fulfillmentInfo?.trackingCode) {
    trackingNumbers.push(row.fulfillmentInfo?.trackingCode);
  } else if (row.shipmentDetails?.freightShipment) {
    trackingNumbers.push(
      row.shipmentDetails.freightShipment.tracker.trackingCode,
    );
  }

  return [...new Set(trackingNumbers)];
}

function groupDataToTrackingLinks(row: FulfillmentGroupData): string[] {
  const trackingLinks = [];
  if (row.shipment?.tracker?.trackingCode) {
    trackingLinks.push(
      getTrackingLink(
        row.shipment?.tracker?.carrier,
        row.shipment?.tracker?.trackingCode,
        row.shipment?.tracker?.publicUrl,
        row.shipment?.boughtRate?.service,
      ),
    );
    for (const additionalShipment of row.additionalShipments ?? []) {
      if (additionalShipment.tracker?.trackingCode) {
        trackingLinks.push(
          getTrackingLink(
            additionalShipment.tracker?.carrier,
            additionalShipment.tracker?.trackingCode,
            additionalShipment.tracker?.publicUrl,
            additionalShipment.boughtRate?.service,
          ),
        );
      }
    }
  } else if (row.fulfillmentInfo?.trackingCode) {
    trackingLinks.push(
      getTrackingLink(
        row.fulfillmentInfo?.carrier,
        row.fulfillmentInfo?.trackingCode,
        "",
        row.fulfillmentInfo?.service || undefined,
      ),
    );
  } else if (row.shipmentDetails?.freightShipment) {
    trackingLinks.push(row.shipmentDetails.freightShipment.tracker.publicUrl);
  }
  return [...new Set(trackingLinks)];
}
